import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Video, Mic, Upload, PenLine, ArrowLeft, RotateCcw, Check, Camera, AlertCircle, Heart, ImagePlus, X, Sparkles, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { chunkedUpload, uploadPhoto, pickMime } from "@/lib/upload";
import { getTheme, themeStyle } from "@/lib/eventTheme";
import { Logo } from "@/components/Common";
import ShareButtons from "@/components/ShareButtons";
import { AIScriptCoach, AIDraftLabel } from "@/components/AICopilot";

// Detect MediaRecorder support once — falls back to native camera capture on iOS Safari < 14.5.
function detectMediaRecorderSupport() {
  if (typeof window === "undefined") return true;
  if (typeof window.MediaRecorder === "undefined") return false;
  try {
    if (window.MediaRecorder.isTypeSupported) {
      return (
        window.MediaRecorder.isTypeSupported("video/mp4") ||
        window.MediaRecorder.isTypeSupported("video/webm") ||
        window.MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
      );
    }
    return true;
  } catch { return false; }
}

function ConsentLine({ eventType, className = "" }) {
  const memorial = eventType === "memorial";
  return (
    <p data-testid="consent-line" className={`flex items-start gap-1.5 text-[11.5px] leading-snug text-[hsl(var(--muted-foreground))] ${className}`}>
      <Shield size={12} className="mt-[2px] shrink-0" />
      <span>
        {memorial
          ? "By submitting, you allow the organizer to include this in the tribute film. Your email is only used for a thank-you and, if you like, when the film is ready — never shared."
          : "By submitting, you allow the organizer to include this in their tribute video. Your email is only used to send you a thank-you and, if you'd like, a link to the finished film."}
      </span>
    </p>
  );
}

function inviteToken(slug) {
  try { return sessionStorage.getItem(`gathervoices_invite_${slug}`) || undefined; } catch { return undefined; }
}

export default function Recorder() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [mode, setMode] = useState(null); // video | audio | upload | text
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("");
  const [done, setDone] = useState(false);
  const [showScriptCoach, setShowScriptCoach] = useState(false);
  const [script, setScript] = useState("");
  const mediaRecorderSupported = useMemo(detectMediaRecorderSupport, []);
  const startedTrackedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/tribute/${slug}`); setEv(res.data); }
      catch { toast.error("Could not load tribute"); navigate(`/t/${slug}`); }
    })();
  }, [slug, navigate]);

  if (!ev) return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;
  const theme = getTheme(ev.type);
  const maxLen = ev.settings?.max_clip_length || 60;

  if (done) return <ThankYou ev={ev} theme={theme} slug={slug} onAnother={() => { setDone(false); setMode(null); }} />;

  const infoValid = name.trim().length > 0;

  // If MediaRecorder isn't supported (older iOS Safari), replace live record modes
  // with a friendly native capture path.
  const modes = [
    { key: "video", label: mediaRecorderSupported ? "Record video" : "Use my camera", icon: mediaRecorderSupported ? Video : Camera, show: true },
    { key: "audio", label: "Audio only", icon: Mic, show: ev.settings?.allow_audio !== false && mediaRecorderSupported },
    { key: "upload", label: "Upload a video", icon: Upload, show: true },
    { key: "text", label: "Write instead", icon: PenLine, show: ev.settings?.allow_text !== false },
  ].filter((m) => m.show);

  const pickMode = (m) => {
    // If MediaRecorder is unsupported and they picked "video", route to native fallback path (uses upload UI with capture=user).
    const finalMode = (!mediaRecorderSupported && m.key === "video") ? "native-capture" : m.key;
    setMode(finalMode);
    if (!startedTrackedRef.current) {
      startedTrackedRef.current = true;
      api.post("/track", { name: "record_start", slug, meta: { mode: finalMode } }).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen" style={themeStyle(ev.type)}>
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <button onClick={() => (mode ? setMode(null) : navigate(`/t/${slug}`))} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={16} /> Back</button>
          <Logo />
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: `hsl(${theme.accent})` }}>{theme.framing} {ev.honoree_name}</p>
          <h1 className="mt-1 font-display text-3xl font-semibold">Share your tribute</h1>
        </div>

        {/* Contributor identity */}
        <div className="mt-6 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5 space-y-4">
          <div>
            <label className="text-sm font-medium">Your name <span className="text-[hsl(var(--destructive))]">*</span></label>
            <input data-testid="contributor-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Margaret Doyle" className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-medium">How did you know them?</label>
            <input data-testid="contributor-relationship-input" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. College friend" className={inputCls} />
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Shown with your tribute in the final film.</p>
          </div>
          <div>
            <label className="text-sm font-medium">Your email <span className="text-[hsl(var(--muted-foreground))] font-normal">(optional)</span></label>
            <input data-testid="contributor-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} inputMode="email" autoComplete="email" />
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">We&apos;ll send a gentle thank-you when the film is ready. Never shared.</p>
          </div>
          {!mediaRecorderSupported && (
            <div data-testid="ios-fallback-hint" className="text-xs text-[hsl(var(--muted-foreground))] rounded-lg bg-[hsl(var(--muted))] p-3 flex items-start gap-2">
              <Camera size={14} className="mt-0.5 shrink-0" />
              <span>Your browser doesn&apos;t support in-app recording. Tap <strong>Use my camera</strong> to record with your phone&apos;s native camera and we&apos;ll upload it.</span>
            </div>
          )}
        </div>

        {/* Mode chooser or active mode */}
        {!mode ? (
          <div className="mt-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{infoValid ? "How would you like to share?" : "Add your name above, then choose how to share."}</p>
            <div className="grid grid-cols-2 gap-3">
              {modes.map((m) => (
                <button key={m.key} data-testid={`mode-${m.key}-button`} disabled={!infoValid} onClick={() => pickMode(m)}
                  className="rounded-2xl border-2 border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 text-left hover:border-[hsl(var(--accent))] transition-colors disabled:opacity-50"
                  style={infoValid ? {} : {}}>
                  <div className="h-11 w-11 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}><m.icon size={20} /></div>
                  <div className="mt-3 font-semibold">{m.label}</div>
                </button>
              ))}
            </div>

            {/* AI script coach entry */}
            <div className="mt-5 rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex items-center gap-3">
              <div className="h-11 w-11 grid place-items-center rounded-xl shrink-0" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>
                <Sparkles size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Not sure what to say?</div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">A short draft you can read on camera. Every word stays yours.</p>
                {script && <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))] truncate">Draft ready · {script.slice(0, 90)}…</div>}
              </div>
              <button
                data-testid="ai-script-open"
                disabled={!infoValid}
                onClick={() => setShowScriptCoach(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-semibold disabled:opacity-50"
              >
                <Sparkles size={14} /> Help me find the words
              </button>
            </div>
          </div>
        ) : mode === "text" ? (
          <TextTribute ev={ev} theme={theme} slug={slug} name={name} email={email} relationship={relationship} initialMessage={script} onDone={() => setDone(true)} />
        ) : (mode === "upload" || mode === "native-capture") ? (
          <UploadTribute ev={ev} theme={theme} slug={slug} name={name} email={email} relationship={relationship} nativeCapture={mode === "native-capture"} onDone={() => setDone(true)} />
        ) : (
          <LiveRecorder ev={ev} theme={theme} slug={slug} name={name} email={email} relationship={relationship} audioOnly={mode === "audio"} maxLen={maxLen} script={script} onDone={() => setDone(true)} />
        )}
      </div>

      {/* AI Script Coach modal */}
      <AIScriptCoach
        open={showScriptCoach}
        onClose={() => setShowScriptCoach(false)}
        slug={slug}
        contributorName={name}
        onApply={(s) => { setScript(s); toast.success("Draft ready — edit before you record."); }}
      />
    </div>
  );
}

const inputCls = "mt-1.5 w-full h-12 rounded-xl px-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";

// ---------------- Live video/audio recorder ----------------
function LiveRecorder({ ev, theme, slug, name, email, relationship, audioOnly, maxLen, script, onDone }) {
  const [phase, setPhase] = useState("permission"); // permission | ready | countdown | recording | review | uploading | error
  const [error, setError] = useState("");
  const [count, setCount] = useState(3);
  const [seconds, setSeconds] = useState(0);
  const [progress, setProgress] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState("");

  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const mimeRef = useRef("");
  const timerRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  useEffect(() => () => { clearInterval(timerRef.current); stopStream(); }, [stopStream]);

  const enable = async () => {
    setError("");
    try {
      const constraints = audioOnly ? { audio: true } : { audio: true, video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (!audioOnly && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        await liveRef.current.play().catch(() => {});
      }
      setPhase("ready");
    } catch (e) {
      let msg = "We couldn't access your camera or microphone.";
      if (e.name === "NotAllowedError") msg = "Permission was denied. Please allow camera & microphone access, then try again.";
      else if (e.name === "NotFoundError") msg = "No camera found. You can switch to audio-only or upload a video instead.";
      setError(msg); setPhase("error");
    }
  };

  const startCountdown = () => {
    setPhase("countdown"); setCount(3);
    let c = 3;
    const iv = setInterval(() => {
      c -= 1;
      if (c <= 0) { clearInterval(iv); beginRecording(); } else setCount(c);
    }, 900);
  };

  const beginRecording = () => {
    const mime = pickMime(audioOnly);
    mimeRef.current = mime || (audioOnly ? "audio/webm" : "video/webm");
    chunksRef.current = [];
    try {
      const rec = mime ? new MediaRecorder(streamRef.current, { mimeType: mime }) : new MediaRecorder(streamRef.current);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        blobRef.current = blob;
        setRecordedUrl(URL.createObjectURL(blob));
        setPhase("review");
      };
      rec.start(1000);
      recorderRef.current = rec;
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => setSeconds((s) => {
        const next = s + 1;
        if (next >= maxLen) stopRecording();
        return next;
      }), 1000);
    } catch (e) {
      setError("Recording could not start on this device. Try uploading a video instead.");
      setPhase("error");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const reRecord = () => { setRecordedUrl(""); blobRef.current = null; setSeconds(0); setPhase("ready"); };

  const submit = async () => {
    setPhase("uploading"); setProgress(0);
    try {
      const ext = mimeRef.current.includes("mp4") ? (audioOnly ? "m4a" : "mp4") : "webm";
      const up = await chunkedUpload(blobRef.current, `tribute.${ext}`, mimeRef.current, setProgress);
      await api.post(`/tribute/${slug}/submissions`, {
        kind: audioOnly ? "audio" : "video", contributor_name: name, contributor_email: email || undefined, relationship,
        file_id: up.file_id, duration: seconds, invite_token: inviteToken(slug), consent: true,
      });
      stopStream();
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed");
      setPhase("error");
    }
  };

  const ringR = 46, ringC = 2 * Math.PI * ringR;
  const ratio = Math.min(seconds / maxLen, 1);

  return (
    <div className="mt-6">
      {phase === "permission" && (
        <div className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-8 text-center">
          <div className="h-14 w-14 mx-auto grid place-items-center rounded-full" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>{audioOnly ? <Mic size={24} /> : <Camera size={24} />}</div>
          <h3 className="mt-4 font-display text-xl font-semibold">{audioOnly ? "Enable your microphone" : "Enable your camera & mic"}</h3>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Your browser will ask for permission. Nothing is recorded until you press record.</p>
          <button data-testid="enable-camera-button" onClick={enable} className="mt-5 w-full h-12 rounded-xl text-white font-semibold" style={{ background: `hsl(${theme.accent})` }}>{audioOnly ? "Enable microphone" : "Enable camera"}</button>
        </div>
      )}

      {phase === "error" && (
        <div className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-8 text-center">
          <AlertCircle className="mx-auto text-[hsl(var(--destructive))]" size={28} />
          <p className="mt-3 text-sm">{error}</p>
          <button onClick={() => setPhase("permission")} className="mt-5 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold">Try again</button>
        </div>
      )}

      {(phase === "ready" || phase === "countdown" || phase === "recording" || phase === "review") && (
        <div className="rounded-2xl overflow-hidden bg-black relative">
          {!audioOnly ? (
            <video ref={liveRef} playsInline className="w-full aspect-[3/4] sm:aspect-video object-cover" style={{ display: phase === "review" ? "none" : "block" }} />
          ) : (
            phase !== "review" && <div className="w-full aspect-video grid place-items-center bg-[hsl(var(--foreground))]"><div className={`h-20 w-20 rounded-full grid place-items-center ${phase === "recording" ? "ever-rec-pulse" : ""}`} style={{ background: `hsl(${theme.accent})` }}><Mic className="text-white" size={30} /></div></div>
          )}

          {phase === "review" && (audioOnly ? (
            <div className="w-full aspect-video grid place-items-center"><audio src={recordedUrl} controls className="w-4/5" /></div>
          ) : (
            <video src={recordedUrl} controls playsInline className="w-full aspect-[3/4] sm:aspect-video object-contain bg-black" />
          ))}

          {phase === "countdown" && (
            <div className="absolute inset-0 grid place-items-center bg-black/40"><span className="font-display text-7xl font-semibold text-white tabular-nums">{count}</span></div>
          )}
          {phase === "recording" && (
            <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5 text-white text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full bg-red-500 ever-rec-pulse" /><span className="tabular-nums">{fmt(seconds)}</span> / {fmt(maxLen)}</div>
          )}

          {/* Teleprompter (AI script) */}
          {script && (phase === "ready" || phase === "countdown" || phase === "recording") && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-black/0 p-4 pt-10 text-white text-[15px] leading-relaxed max-h-[45%] overflow-y-auto" data-testid="teleprompter">
              <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mb-1 inline-flex items-center gap-1"><Sparkles size={11} /> Your draft — glance, don&apos;t read robotically</div>
              <p className="whitespace-pre-wrap">{script}</p>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      {phase === "ready" && (
        <button data-testid="recorder-start-button" onClick={startCountdown} className="mt-5 w-full h-14 rounded-2xl text-white text-lg font-semibold inline-flex items-center justify-center gap-2" style={{ background: `hsl(${theme.accent})` }}>
          <span className="h-4 w-4 rounded-full bg-white" /> Start recording
        </button>
      )}
      {phase === "recording" && (
        <div className="mt-5 flex flex-col items-center">
          <button data-testid="recorder-stop-button" onClick={stopRecording} className="relative h-20 w-20 grid place-items-center">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100"><circle cx="50" cy="50" r={ringR} fill="none" stroke="hsl(var(--border))" strokeWidth="6" /><circle cx="50" cy="50" r={ringR} fill="none" stroke={`hsl(${theme.accent})`} strokeWidth="6" strokeLinecap="round" strokeDasharray={ringC} strokeDashoffset={ringC * (1 - ratio)} /></svg>
            <span className="h-7 w-7 rounded-md bg-red-600" />
          </button>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Tap to stop</p>
        </div>
      )}
      {phase === "review" && (
        <>
          <div className="mt-5 flex gap-3">
            <button data-testid="recorder-rerecord-button" onClick={reRecord} className="flex-1 h-13 py-3.5 rounded-xl border border-[hsl(var(--border))] font-semibold inline-flex items-center justify-center gap-2"><RotateCcw size={18} /> Re-record</button>
            <button data-testid="recorder-submit-button" onClick={submit} className="flex-1 py-3.5 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2" style={{ background: `hsl(${theme.accent})` }}><Check size={18} /> Submit tribute</button>
          </div>
          <ConsentLine eventType={ev.type} className="mt-3" />
        </>
      )}
      {phase === "uploading" && (
        <div className="mt-6 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 text-center">
          <p className="font-semibold">Sending your tribute…</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Please keep this tab open.</p>
          <div className="mt-4 h-3 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full transition-all" style={{ width: `${progress}%`, background: `hsl(${theme.accent})` }} /></div>
          <p className="mt-2 text-sm font-semibold tabular-nums">{progress}%</p>
        </div>
      )}
    </div>
  );
}

// ---------------- Upload existing file (also used as iOS native camera fallback) ----------------
function UploadTribute({ ev, theme, slug, name, email, relationship, nativeCapture, onDone }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const [fileName, setFileName] = useState("");

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast.error("This file is a little too large — please pick one under 500 MB."); return; }
    setFileName(file.name); setProgress(0);
    try {
      const up = await chunkedUpload(file, file.name, file.type || "video/mp4", setProgress);
      const kind = (file.type || "").startsWith("audio") ? "audio" : "video";
      await api.post(`/tribute/${slug}/submissions`, {
        kind, contributor_name: name, contributor_email: email || undefined, relationship,
        file_id: up.file_id, invite_token: inviteToken(slug), consent: true,
      });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong sending your tribute. Please try again."); setProgress(null); }
  };

  return (
    <div className="mt-6 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6">
      {progress === null ? (
        <>
          <button data-testid={nativeCapture ? "native-capture-button" : "upload-dropzone"} onClick={() => ref.current?.click()} className="w-full rounded-xl border-2 border-dashed border-[hsl(var(--border))] p-10 text-center hover:bg-[hsl(var(--muted))] transition-colors">
            {nativeCapture ? <Camera className="mx-auto" style={{ color: `hsl(${theme.accent})` }} /> : <Upload className="mx-auto" style={{ color: `hsl(${theme.accent})` }} />}
            <p className="mt-3 font-semibold">{nativeCapture ? "Open my camera" : "Choose a video or audio file"}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{nativeCapture ? "Your phone will open its native camera. When you're happy, tap Done and we'll upload it." : "mp4, mov, webm · up to 500MB"}</p>
          </button>
          <ConsentLine eventType={ev.type} className="mt-4" />
        </>
      ) : (
        <div className="text-center">
          <p className="font-semibold truncate">{fileName}</p>
          <div className="mt-4 h-3 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full transition-all" style={{ width: `${progress}%`, background: `hsl(${theme.accent})` }} /></div>
          <p className="mt-2 text-sm font-semibold tabular-nums">{progress}%</p>
          <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">Please keep this tab open.</p>
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept={nativeCapture ? "video/*" : "video/*,audio/*"}
        {...(nativeCapture ? { capture: "user" } : {})}
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

// ---------------- Written tribute ----------------
function TextTribute({ ev, theme, slug, name, email, relationship, initialMessage, onDone }) {
  const ref = useRef(null);
  const [message, setMessage] = useState(initialMessage || "");
  const [photoId, setPhotoId] = useState(null);
  const [saving, setSaving] = useState(false);

  const addPhoto = async (file) => {
    if (!file) return;
    try { const { file_id } = await uploadPhoto(file); setPhotoId(file_id); } catch { toast.error("Could not upload photo"); }
  };

  const submit = async () => {
    if (!message.trim()) { toast.error("Please write a few words"); return; }
    setSaving(true);
    try {
      await api.post(`/tribute/${slug}/submissions`, {
        kind: "text", contributor_name: name, contributor_email: email || undefined, relationship,
        message, photo_file_id: photoId, invite_token: inviteToken(slug), consent: true,
      });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong sending your tribute. Please try again."); setSaving(false); }
  };

  return (
    <div className="mt-6 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Your message</label>
        {initialMessage && <AIDraftLabel />}
      </div>
      <textarea data-testid="text-tribute-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} placeholder={`Share a memory of ${ev.honoree_name}…`} className="mt-1.5 w-full rounded-xl p-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none leading-relaxed" />
      <div className="mt-4 flex items-center gap-3">
        {photoId ? (
          <div className="relative h-20 w-20 rounded-xl overflow-hidden border border-[hsl(var(--border))]"><img src={fileUrl(photoId)} alt="" className="h-full w-full object-cover" /><button onClick={() => setPhotoId(null)} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white"><X size={13} /></button></div>
        ) : (
          <button data-testid="text-add-photo-button" onClick={() => ref.current?.click()} className="h-20 w-20 grid place-items-center rounded-xl border-2 border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"><ImagePlus size={20} /></button>
        )}
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Add a photo (optional)</p>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => addPhoto(e.target.files?.[0])} />
      </div>
      <button data-testid="text-submit-button" disabled={saving} onClick={submit} className="mt-5 w-full h-12 rounded-xl text-white font-semibold disabled:opacity-60" style={{ background: `hsl(${theme.accent})` }}>{saving ? "Sending…" : "Submit written tribute"}</button>
      <ConsentLine eventType={ev.type} className="mt-3" />
    </div>
  );
}

// ---------------- Thank you ----------------
function ThankYou({ ev, theme, slug, onAnother }) {
  const url = `${window.location.origin}/t/${slug}`;
  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" style={themeStyle(ev.type)}>
      <div className="max-w-md w-full text-center ever-fade-in">
        <div className="h-16 w-16 mx-auto grid place-items-center rounded-full" style={{ background: `hsl(${theme.accent})` }}><Heart className="text-white" size={28} /></div>
        <h1 className="mt-5 font-display text-3xl font-semibold">Thank you</h1>
        <p className="mt-2 text-[hsl(var(--muted-foreground))]">Your tribute for <strong>{ev.honoree_name}</strong> has been received{ev.settings?.moderation ? " and will be reviewed by the family" : ""}.</p>

        <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 text-left">
          <p className="font-display text-lg font-semibold text-center">Know someone else who&apos;d like to contribute?</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] text-center mt-1 mb-4">Pass it on — every voice adds to the story.</p>
          <div className="flex justify-center"><ShareButtons type={ev.type} name={ev.honoree_name} url={url} accent={theme.accent} testidPrefix="contributor-share" /></div>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <button data-testid="record-another-button" onClick={onAnother} className="h-12 rounded-xl text-white font-semibold" style={{ background: `hsl(${theme.accent})` }}>Record another</button>
          <Link to={`/t/${slug}`} className="text-sm text-[hsl(var(--muted-foreground))] underline mt-1">Back to tribute page</Link>
        </div>
      </div>
    </div>
  );
}

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
